/**
 * A compact, curated emoji set for sticker associations — enough common emoji
 * for a mobile picker without shipping the full ~3.6k Unicode table. Each entry
 * is `[emoji, keywords]` for search. Category labels are localized in the UI.
 */
export interface EmojiCategory {
  /** Localization key suffix under `emoji.categories.*`. */
  key: string;
  emojis: Array<[string, string]>;
}

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    key: 'smileys',
    emojis: [
      ['😀', 'grin happy'], ['😁', 'grin'], ['😂', 'lol laugh cry'], ['🤣', 'rofl laugh'],
      ['😊', 'smile blush'], ['😇', 'angel'], ['🙂', 'smile'], ['😉', 'wink'],
      ['😍', 'love heart eyes'], ['🥰', 'love'], ['😘', 'kiss'], ['😜', 'tongue'],
      ['🤪', 'crazy'], ['😎', 'cool sunglasses'], ['🤩', 'star struck'], ['🥳', 'party'],
      ['😏', 'smirk'], ['😢', 'cry sad'], ['😭', 'sob cry'], ['😤', 'angry'],
      ['😠', 'angry mad'], ['🤔', 'thinking'], ['🤨', 'suspicious'], ['😴', 'sleep'],
      ['🤯', 'mind blown'], ['😱', 'scream shock'], ['🥺', 'pleading'], ['😅', 'sweat'],
    ],
  },
  {
    key: 'gestures',
    emojis: [
      ['👍', 'thumbs up like'], ['👎', 'thumbs down'], ['👌', 'ok'], ['✌️', 'peace victory'],
      ['🤞', 'fingers crossed'], ['🤙', 'call me'], ['👊', 'fist'], ['✊', 'fist'],
      ['👏', 'clap'], ['🙌', 'raise hands'], ['🙏', 'pray thanks'], ['💪', 'muscle strong'],
      ['👋', 'wave hello'], ['🤝', 'handshake'], ['✍️', 'writing'], ['👉', 'point'],
    ],
  },
  {
    key: 'people',
    emojis: [
      ['😺', 'cat smile'], ['👶', 'baby'], ['🧑', 'person'], ['👑', 'crown king'],
      ['🦸', 'hero'], ['🧙', 'wizard'], ['🧚', 'fairy'], ['👻', 'ghost boo'],
      ['🤖', 'robot'], ['👽', 'alien'], ['💀', 'skull'], ['🎃', 'pumpkin'],
    ],
  },
  {
    key: 'animals',
    emojis: [
      ['🐶', 'dog puppy'], ['🐱', 'cat kitten'], ['🐭', 'mouse'], ['🐰', 'rabbit bunny'],
      ['🦊', 'fox'], ['🐻', 'bear'], ['🐼', 'panda'], ['🐨', 'koala'],
      ['🦁', 'lion'], ['🐯', 'tiger'], ['🐸', 'frog'], ['🐵', 'monkey'],
      ['🦄', 'unicorn'], ['🐝', 'bee'], ['🦋', 'butterfly'], ['🐢', 'turtle'],
    ],
  },
  {
    key: 'food',
    emojis: [
      ['🍎', 'apple'], ['🍌', 'banana'], ['🍕', 'pizza'], ['🍔', 'burger'],
      ['🍟', 'fries'], ['🌮', 'taco'], ['🍩', 'donut'], ['🍪', 'cookie'],
      ['🎂', 'cake birthday'], ['🍰', 'cake'], ['🍫', 'chocolate'], ['🍿', 'popcorn'],
      ['☕', 'coffee'], ['🍺', 'beer'], ['🥤', 'drink'], ['🍦', 'ice cream'],
    ],
  },
  {
    key: 'activity',
    emojis: [
      ['⚽', 'soccer football'], ['🏀', 'basketball'], ['🎮', 'game'], ['🎲', 'dice'],
      ['🎯', 'target'], ['🎨', 'art'], ['🎵', 'music'], ['🎸', 'guitar'],
      ['🏆', 'trophy win'], ['🥇', 'gold medal'], ['🎁', 'gift'], ['🎉', 'party tada'],
    ],
  },
  {
    key: 'travel',
    emojis: [
      ['🚗', 'car'], ['✈️', 'plane flight'], ['🚀', 'rocket'], ['🚲', 'bike bicycle'],
      ['🏝️', 'island beach'], ['🏔️', 'mountain'], ['🗺️', 'map travel'], ['🧳', 'luggage'],
      ['🚢', 'ship boat'], ['🚂', 'train'], ['🗼', 'tower'], ['🎡', 'ferris wheel'],
      ['🏖️', 'beach'], ['⛺', 'tent camping'], ['🌍', 'earth globe'], ['🚁', 'helicopter'],
    ],
  },
  {
    key: 'objects',
    emojis: [
      ['💡', 'idea light'], ['🔥', 'fire lit'], ['💯', 'hundred'], ['⭐', 'star'],
      ['🌟', 'star glow'], ['✨', 'sparkles'], ['💥', 'boom'], ['💫', 'dizzy'],
      ['📱', 'phone'], ['💻', 'laptop'], ['📷', 'camera'], ['💎', 'diamond gem'],
      ['🎀', 'bow ribbon'], ['🫧', 'bubbles'], ['🌙', 'moon'], ['☀️', 'sun'],
    ],
  },
  {
    key: 'hearts',
    emojis: [
      ['❤️', 'red heart love'], ['🧡', 'orange heart'], ['💛', 'yellow heart'], ['💚', 'green heart'],
      ['💙', 'blue heart'], ['💜', 'purple heart'], ['🖤', 'black heart'], ['🤍', 'white heart'],
      ['💖', 'sparkle heart'], ['💗', 'growing heart'], ['💕', 'two hearts'], ['💔', 'broken heart'],
    ],
  },
  {
    key: 'symbols',
    emojis: [
      ['✅', 'check yes'], ['❌', 'cross no'], ['❓', 'question'], ['❗', 'exclamation'],
      ['➕', 'plus'], ['♻️', 'recycle'], ['⚠️', 'warning'], ['🔞', 'no under 18'],
      ['🆗', 'ok'], ['🎶', 'notes music'], ['💬', 'speech bubble'], ['🔔', 'bell'],
    ],
  },
  {
    key: 'flags',
    emojis: [
      ['🏳️', 'white flag'], ['🏴', 'black flag'], ['🏁', 'checkered finish'], ['🚩', 'triangular flag'],
      ['🏳️‍🌈', 'rainbow pride'], ['🇮🇷', 'iran'], ['🇷🇺', 'russia'], ['🇺🇸', 'usa united states'],
      ['🇬🇧', 'uk britain'], ['🇩🇪', 'germany'], ['🇫🇷', 'france'], ['🇹🇷', 'turkey'],
      ['🇯🇵', 'japan'], ['🇰🇷', 'korea'], ['🇨🇳', 'china'], ['🇮🇳', 'india'],
    ],
  },
];

/** Flat list for searching. */
export const ALL_EMOJIS: Array<[string, string]> = EMOJI_CATEGORIES.flatMap((c) => c.emojis);
